import { clsx } from 'clsx';
import { Tooltip } from 'radix-ui';
import type { ComponentPropsWithoutRef } from 'react';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type TooltipContentProps = ComponentPropsWithoutRef<typeof Tooltip.Content> & {
  /** Whether to render the content inside a Portal (recommended — avoids z-index/overflow clipping). Defaults to true. */
  withPortal?: boolean;
  /** Whether to render the Radix arrow. Defaults to true. */
  withArrow?: boolean;
};

/* -------------------------------------------------------------------------- */
/*                               Main components                              */
/* -------------------------------------------------------------------------- */

/**
 * Pre-styled tooltip content with consistent design-system tokens.
 * Wrap with `TooltipRoot` and place your trigger inside `TooltipTrigger`.
 * Expects a `Tooltip.Provider` ancestor (already set up in `App.tsx`).
 *
 * @example
 * ```tsx
 * <TooltipRoot>
 *   <TooltipTrigger asChild>
 *     <button>Hover me</button>
 *   </TooltipTrigger>
 *   <TooltipContent>Helpful description</TooltipContent>
 * </TooltipRoot>
 * ```
 */
const TooltipContent = ({
  className,
  children,
  side = 'top',
  align = 'center',
  withPortal = true,
  withArrow = true,
  ...props
}: TooltipContentProps) => {
  const content = (
    <Tooltip.Content
      side={side}
      align={align}
      className={clsx(
        'wrap-break-word max-w-60 text-balance rounded-md bg-accent-foreground p-2 text-primary-foreground text-xs',
        className,
      )}
      {...props}
    >
      {children}
      {withArrow && <Tooltip.Arrow className="fill-accent-foreground" />}
    </Tooltip.Content>
  );

  return withPortal ? <Tooltip.Portal>{content}</Tooltip.Portal> : content;
};
TooltipContent.displayName = 'TooltipContent';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export const TooltipRoot = Tooltip.Root;
export const TooltipTrigger = Tooltip.Trigger;

export type { TooltipContentProps };
export { TooltipContent };
